import { memo, useCallback, useEffect, useState, type MouseEvent } from 'react'
import { type ActiveChatEntry, getTurnStartTime, removeCompletedTurn } from '@renderer/chat/store/turn-status'
import { StreamingChatIndicator } from '@renderer/components/streaming-chat-indicator'
import { useViewActions } from '../../../view-atoms'

export function ElapsedTime({ startTime }: { startTime: number | null }) {
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    if (!startTime) return
    const tick = () => setElapsed(Math.floor((Date.now() - startTime) / 1000))
    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [startTime])

  if (!startTime) return null
  if (elapsed < 60) return <span>{elapsed}s</span>
  const mins = Math.floor(elapsed / 60)
  const secs = elapsed % 60
  return (
    <span>
      {mins}m {secs}s
    </span>
  )
}

interface ActiveChatCellProps {
  chat: ActiveChatEntry
}

export const ActiveChatCell = memo(function ActiveChatCell({ chat }: ActiveChatCellProps) {
  const { openView } = useViewActions()
  const isStreaming = chat.status === 'streaming'

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      openView('chat', { chatId: chat.id, instantScrollToBottom: true }, { sourceLabel: 'Home', pointerEvent: event })
      if (chat.status === 'completed') {
        removeCompletedTurn(chat.id)
      }
    },
    [openView, chat.id, chat.status],
  )

  const displayStartTime = isStreaming ? getTurnStartTime(chat.id) : null

  // Flat row, not a card: these live *inside* the chat input's card now, so the
  // input's own border and shadow are the container chrome. Left inset lines the
  // status dot up with the context chips below it.
  return (
    <button
      onClick={handleClick}
      className="hover:bg-element-hover text-foreground corner-smooth type-ui-sm flex w-full cursor-pointer items-center gap-2.5 rounded-xl bg-transparent px-1.5 py-1.5 text-left font-normal transition-colors"
    >
      {isStreaming ? (
        <StreamingChatIndicator label={`${chat.title} is working`} size={16} />
      ) : (
        <div className="flex size-4 shrink-0 items-center justify-center">
          <div className="bg-blue-9 size-2.5 rounded-full" />
        </div>
      )}
      <span className="min-w-0 flex-1 truncate">{chat.title}</span>
      {isStreaming && (
        <span className="text-foreground-secondary type-ui-xs flex-shrink-0">
          <ElapsedTime startTime={displayStartTime} />
        </span>
      )}
    </button>
  )
})
